// The phone warning (architecture sprint-14 §8.4, Decision 16). Shown on a phone when a start option has alarms;
// Start stays allowed. With `onOpenSop` (the shift card, sprint 16) the "Before your shift" link opens the
// checklist dialog. With `sopPopover` (the quick-add sheet, owner addition in sprint 15) it opens a large,
// non-modal popover with the SOP steps on tap (or hover with a mouse), because the sheet is itself a modal. Without
// either it navigates to the SOP page, which stays for sharing.
import { Smartphone } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SopSteps } from '@/components/alarms/SopSteps';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SOP_PATH, SOP_TITLE } from '@/content/shift-sop';
import { isPhoneDevice, currentDeviceSignals } from '@/lib/device';

export const PHONE_WARNING = "Alarms don't work on phones. Use your laptop for this shift.";

interface PhoneAlarmWarningProps {
  show: boolean;
  onNavigate?: () => void;
  onOpenSop?: () => void;
  /** Sprint 15: show the SOP steps in a tap-to-open popover instead of a link. */
  sopPopover?: boolean;
}

/** Tap toggles it (it stays until tapped again or dismissed); hovering opens it, leaving closes it unless clicked. */
export function SopPopover(): JSX.Element {
  const [open, setOpen] = useState(false);
  const byHover = useRef(false);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        byHover.current = false;
        setOpen(o);
      }}
    >
      <PopoverTrigger
        className="underline"
        onMouseEnter={() => {
          if (open) return;
          byHover.current = true;
          setOpen(true);
        }}
        onMouseLeave={() => {
          if (byHover.current) setOpen(false);
        }}
        onClick={(e) => {
          // Opened by hover (a mouse, or a phone emulating mouseenter before the tap): the click pins it open.
          if (byHover.current) {
            byHover.current = false;
            e.preventDefault();
          }
        }}
      >
        {SOP_TITLE}
      </PopoverTrigger>
      <PopoverContent
        align="center"
        collisionPadding={16}
        aria-label={SOP_TITLE}
        className="max-h-[70vh] w-[min(28rem,calc(100vw-2rem))] overflow-y-auto p-4"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <p className="mb-3 text-sm font-medium text-foreground">{SOP_TITLE}</p>
        <SopSteps />
      </PopoverContent>
    </Popover>
  );
}

export function PhoneAlarmWarning({ show, onNavigate, onOpenSop, sopPopover = false }: PhoneAlarmWarningProps): JSX.Element | null {
  if (!show || !isPhoneDevice(currentDeviceSignals())) return null;
  return (
    <p role="note" className="flex items-start gap-2 rounded-lg bg-warning px-3 py-2 text-xs text-warning-fg">
      <Smartphone className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {PHONE_WARNING}{' '}
        {onOpenSop ? (
          <button type="button" className="underline" aria-haspopup="dialog" onClick={onOpenSop}>
            {SOP_TITLE}
          </button>
        ) : sopPopover ? (
          <SopPopover />
        ) : (
          <Link to={SOP_PATH} className="underline" onClick={onNavigate}>
            {SOP_TITLE}
          </Link>
        )}
      </span>
    </p>
  );
}
